import React, { ReactNode } from 'react';
import moment from "moment";
import { useIntl } from "react-intl";
import { TableColumn } from 'react-data-table-component';
import BadgeTooltip from '../../../components/General/BadgeTooltip';
import { DataTableExport } from '../../../components/DataTable/DataTableExport';
import { UserModel } from '../../modules/auth/models/UserModel';
import { shallowEqual, useSelector } from 'react-redux';
import { RootState } from '../../../setup';
import { FilterMerchantPIC } from '../../../components/Merchants/FilterMerchantPIC';
import { MasterDataTable } from '../../../components/DataTable/MasterDataTable';

type SourceLog = 'ROUND ROBIN' | 'MAIN UPDATE' | 'MAIN CREATE' | 'AUTO CREATE' | 'IMPORT EXCEL' | 'UNKNOWN';
type PICLog = {
    id: string;
    userIdBefore: string,
    userNameBefore: string,
    userIdAfter: string,
    userNameAfter: string,
    merchantId: string;
    merchantName: string;
    dateCreated: string;
    source: SourceLog;
};

interface MerchantPICLogProps {
    date: string
    merchantTitle: string
    merchantTemplateId: string
    logs?: PICLog[];
}

const MerchantPICLog: React.FC<MerchantPICLogProps> = ({ date, merchantTitle, merchantTemplateId }) => {
    const intl = useIntl()
    const user: UserModel = useSelector<RootState>(({ auth }) => auth.user, shallowEqual) as UserModel

    const getSourceDescription = (source: PICLog['source']): string => {
        switch (source) {
            case 'ROUND ROBIN':
                return intl.formatMessage({ id: 'MERCHANT.SOURCE.ROUND_ROBIN' }, { title: merchantTitle });
            case 'MAIN UPDATE':
                return intl.formatMessage({ id: 'MERCHANT.SOURCE.MAIN_UPDATE' }, { title: merchantTitle });
            case 'MAIN CREATE':
                return intl.formatMessage({ id: 'MERCHANT.SOURCE.MAIN_CREATE' }, { title: merchantTitle });
            case 'AUTO CREATE':
                return intl.formatMessage({ id: 'MERCHANT.SOURCE.AUTO_CREATE' }, { title: merchantTitle });
            case 'IMPORT EXCEL':
                return intl.formatMessage({ id: 'MERCHANT.SOURCE.IMPORT_EXCEL' }, { title: merchantTitle });
            default:
                return intl.formatMessage({ id: 'MERCHANT.SOURCE.UNKNOWN' });
        }
    };

    const getSourceColor = (source: PICLog['source']): string => {
        switch (source) {
            case 'ROUND ROBIN':
                return 'badge-light-dark';
            case 'MAIN UPDATE':
                return 'badge-light-info';
            case 'MAIN CREATE':
                return 'badge-light-primary';
            case 'AUTO CREATE':
                return 'badge-light-warning';
            case 'IMPORT EXCEL':
                return 'badge-light-success';
            default:
                return 'badge-light-danger';
        }
    };

    const tableColumns: TableColumn<PICLog>[] = [
        {
            name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.TIME' }),
            width: '150px',
            selector: row => row.dateCreated,
            cell: (row) => {
                return moment(row.dateCreated).format("DD MMMM YYYY HH:mm");
            },
            sortable: true,
        },
        {
            name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.NAME_TITLE' }, { title: merchantTitle }),
            width: '200px',
            selector: row => row.merchantName,
            cell: (row) => {
                return row.merchantName;
            },
            sortable: true,
        },
        {
            name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.PIC_BEFORE' }),
            width: '250px',
            selector: row => row.userNameBefore?.toLowerCase(),
            cell: (row) => {
                return row.userNameBefore ?? '-';
            },
            sortable: true,
        },
        {
            name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.PIC_AFTER' }),
            width: '250px',
            selector: row => row.userNameAfter?.toLowerCase(),
            cell: (row) => {
                return row.userNameAfter ?? '-';
            },
            sortable: true,
        },
        {
            name: intl.formatMessage({ id: 'MASTERS.DATATABLE.COLUMNS.PIC_SOURCE' }),
            selector: row => row.source,
            cell: (row) => {
                const badgeColor = getSourceColor(row.source as SourceLog);
                const badgeContent = row.source;

                return (
                    <BadgeTooltip tooltipText={getSourceDescription(row.source as SourceLog)} badgeClass={`my-2 mx-1 ${badgeColor}`}>
                        {badgeContent}
                    </BadgeTooltip>
                );
            },
            sortable: true,
        },
    ];

    let actions: ReactNode[] = []
    if (user.data.is_allowed_export_excel) {
        actions.push(<DataTableExport source={"pic-merchant"} columns={[]} />)
    }
    actions.push(<FilterMerchantPIC merchantTemplateId={merchantTemplateId} />)

    return (
        <MasterDataTable
            tableKey={"pic-merchant"}
            tableColumns={tableColumns}
            actions={actions}
            apiURL={`merchant-owner-movements?merchantTemplateId=${merchantTemplateId}&dateCreated=${date}`}
            orderBy={1}
            order="desc"
            autoReload={false}
        />
    );
};

export { MerchantPICLog };